// One-shot, in-memory hand-off of a just-finished session's live captions from RecordScreen to
// RecordingDetail, so a batch-authoritative recording doesn't show a blank gap while the batch
// pass runs (30s+ of "Transcription in progress" with everything the user just watched being
// transcribed gone from the screen).
//
// Deliberately module-level and NOT persisted (no sessionStorage/localStorage): transcript text
// must not be written to disk anywhere, and losing the carry-over on reload is the correct,
// honest failure — the page falls back to the plain "Transcription in progress" message.
//
// NEVER log the segments — this module contains no logging at all, by design.
import type { StreamingSegment } from '@scribe/core/streaming';

let pending: { recordingId: string; segments: StreamingSegment[] } | null = null;

/** Stash `segments` for the next render of `recordingId`'s detail screen. Replaces any
 * previous stash — only the most recent session's captions can still be relevant. */
export function stashPendingCaptions(recordingId: string, segments: StreamingSegment[]): void {
  pending = { recordingId, segments };
}

/** Returns and clears the stash iff it belongs to `recordingId`, so captions can never leak
 * onto a different recording and are never resurrected by a later refetch. */
export function takePendingCaptions(recordingId: string): StreamingSegment[] | null {
  if (!pending || pending.recordingId !== recordingId) return null;
  const { segments } = pending;
  pending = null;
  return segments;
}
